import { supabase } from '../supabase';
import {
  useQuery,
  useMutation,
  useQueryClient
} from '@tanstack/react-query';
import { ModelResetTimer } from '@shiftly/shared/src/schema';
import { useScheduleShifts } from '../schedule-shifts/supabaseScheduleShiftService';
import { Permissions } from '@shiftly/shared/src/permissions';
import { DateTime } from 'luxon';

interface ModelResetTimerRow {
  id: string;
  model_id: string;
  time: string;
  created_at: string;
  updated_at: string;
}

class SupabaseModelResetTimesService {
  async getModelResetTimer(model_id: string): Promise<ModelResetTimer | null> {
    const { data, error } = await supabase
      .from('model_reset_timers')
      .select('*')
      .eq('model_id', model_id)
      .maybeSingle();

    if (error) {
      if (error.code === 'PGRST116') { // Not found
        return null;
      }
      throw new Error(`Failed to get model reset timer: ${error.message}`);
    }

    if (!data) return null;

    const row = data as ModelResetTimerRow;

    // Parse the time string and create a DateTime in Europe/London timezone
    const [hours, minutes, seconds] = row.time.split(':').map(Number);
    const time = DateTime.fromObject(
      { hour: hours, minute: minutes, second: seconds || 0 },
      { zone: 'Europe/London' }
    );

    return {
      id: row.id,
      model_id: row.model_id,
      time,
      created_at: DateTime.fromISO(row.created_at, { zone: 'UTC' }),
      updated_at: DateTime.fromISO(row.updated_at, { zone: 'UTC' }),
    } as ModelResetTimer;
  }

  async updateModelResetTimer(model_id: string, timer: Partial<ModelResetTimer>) {
    // Check if timer exists
    const currentTimer = await this.getModelResetTimer(model_id);

    const updateData: any = {
      updated_at: DateTime.utc()
    };

    // Convert DateTime to time string if provided
    if (timer.time) {
      updateData.time = timer.time.toFormat('HH:mm:ss');
    }

    if (!currentTimer) {
      // Create new timer if it doesn't exist
      const { error } = await supabase
        .from('model_reset_timers')
        .insert({
          model_id,
          time: timer.time ? timer.time.toFormat('HH:mm:ss') : '00:00:00',
          created_at: DateTime.utc(),
          updated_at: DateTime.utc()
        });

      if (error) {
        throw new Error(`Failed to create model reset timer: ${error.message}`);
      }
    } else {
      // Update existing timer
      const { error } = await supabase
        .from('model_reset_timers')
        .update(updateData)
        .eq('model_id', model_id);

      if (error) {
        throw new Error(`Failed to update model reset timer: ${error.message}`);
      }
    }
  }

  async createModelResetTimer(model_id: string, timer: Omit<ModelResetTimer, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('model_reset_timers')
      .insert({
        model_id,
        time: timer.time.toFormat('HH:mm:ss'),
        created_at: DateTime.utc(),
        updated_at: DateTime.utc()
      })
      .select()
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to create model reset timer: ${error.message}`);
    }

    return data.id;
  }

  async getAllModelResetTimers(): Promise<ModelResetTimer[]> {
    const { data, error } = await supabase
      .from('model_reset_timers')
      .select('*');

    if (error) {
      throw new Error(`Failed to get all model reset timers: ${error.message}`);
    }

    return (data || []).map(row => {
      const [hours, minutes, seconds] = row.time.split(':').map(Number);
      const time = DateTime.fromObject(
        { hour: hours, minute: minutes, second: seconds || 0 },
        { zone: 'Europe/London' }
      );

      return {
        id: row.id,
        model_id: row.model_id,
        time,
        created_at: DateTime.fromISO(row.created_at, { zone: 'UTC' }),
        updated_at: DateTime.fromISO(row.updated_at, { zone: 'UTC' }),
      };
    });
  }
}

const instance = new SupabaseModelResetTimesService();

// Custom hook to check if user can edit reset timer
export const useCanEditResetTimer = (user_id: string, permissions: string[], model_id: string) => {
  const hasAdminPermission = permissions.includes(Permissions.SCHEDULE_EDIT);
  if (hasAdminPermission) return true;

  const today = DateTime.now().setZone('Europe/London');

  const startDate = today.startOf('day');
  const endDate = today.endOf('day');

  const filters = [
    { field: 'user_id', operator: 'eq' as const, value: user_id },
    { field: 'model_id', operator: 'eq' as const, value: model_id },
    { field: 'type', operator: 'eq' as const, value: 'MASS_DM' },
    { field: 'date', operator: 'gte' as const, value: startDate.toISO() },
    { field: 'date', operator: 'lte' as const, value: endDate.toISO() }
  ];

  const { data: todayShifts = [] } = useScheduleShifts({
    filters
  });

  const fpFilters = [
    { field: 'user_id', operator: 'eq' as const, value: user_id },
    { field: 'model_id', operator: 'eq' as const, value: model_id },
    { field: 'type', operator: 'eq' as const, value: 'FP_MASS_DM' },
    { field: 'date', operator: 'gte' as const, value: startDate.toISO() },
    { field: 'date', operator: 'lte' as const, value: endDate.toISO() }
  ];

  const { data: fpTodayShifts = [] } = useScheduleShifts({
    filters: fpFilters
  });

  const hasEditPermission = permissions.includes(Permissions.SCHEDULE_EDIT_RESET_TIMER);
  const hasMassDMShiftToday = todayShifts.length > 0 || fpTodayShifts.length > 0;

  return hasEditPermission && hasMassDMShiftToday;
};

// React Query Hooks
export const useModelResetTimer = (model_id: string) => {
  return useQuery({
    queryKey: ['modelResetTimers', model_id],
    queryFn: () => instance.getModelResetTimer(model_id),
    enabled: !!model_id,
  });
};

export const useAllModelResetTimers = () => {
  return useQuery({
    queryKey: ['allModelResetTimers'],
    queryFn: () => instance.getAllModelResetTimers(),
  });
};

export const useUpdateModelResetTimer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ model_id, timer }: { model_id: string; timer: Partial<ModelResetTimer> }) =>
      instance.updateModelResetTimer(model_id, timer),
    onSuccess: (_, { model_id }) => {
      queryClient.invalidateQueries({ queryKey: ['modelResetTimers', model_id] });
      queryClient.invalidateQueries({ queryKey: ['allModelResetTimers'] });
    },
  });
};

export const useCreateModelResetTimer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ model_id, timer }: { model_id: string; timer: Omit<ModelResetTimer, 'id' | 'created_at' | 'updated_at'> }) =>
      instance.createModelResetTimer(model_id, timer),
    onSuccess: (_, { model_id }) => {
      queryClient.invalidateQueries({ queryKey: ['modelResetTimers', model_id] });
      queryClient.invalidateQueries({ queryKey: ['allModelResetTimers'] });
    },
  });
};

export default instance;